import { useEffect, useState } from "react";

export interface WatchItem {
	conditionId: string;
	question: string;
	addedAt: number;
	/** First outcome's price when pinned. Absent on anything pinned before this was kept. */
	addedPrice?: number | null;
	outcome?: string;
}

export const WATCH_MAX = 200;
const QUESTION_MAX = 300;
const OUTCOME_MAX = 60;
const CONDITION_RE = /^0x[0-9a-fA-F]{64}$/;

const KEY = "polygains:watchlist:v1";
const EVENT = "polygains:watchlist";

export function parseWatchlist(raw: string | null): WatchItem[] {
	if (!raw) return [];
	try {
		const list = JSON.parse(raw);
		if (!Array.isArray(list)) return [];
		return list.filter(
			(i): i is WatchItem =>
				typeof i?.conditionId === "string" && typeof i?.question === "string",
		);
	} catch {
		return [];
	}
}

/** Add when absent, remove when present (pure). */
export function toggleItem(list: WatchItem[], item: Omit<WatchItem, "addedAt">, now: number): WatchItem[] {
	return list.some((i) => i.conditionId === item.conditionId)
		? list.filter((i) => i.conditionId !== item.conditionId)
		: [{ ...item, addedAt: now }, ...list];
}

export function exportWatchlist(items: WatchItem[], nowMs: number): string {
	return JSON.stringify({ version: 1, exported_at: new Date(nowMs).toISOString(), items }, null, 2);
}

function cleanItem(raw: unknown, nowMs: number): WatchItem | null {
	const i = raw as Partial<WatchItem> | null;
	if (typeof i?.conditionId !== "string" || !CONDITION_RE.test(i.conditionId)) return null;
	if (typeof i.question !== "string" || !i.question.trim()) return null;
	const item: WatchItem = {
		conditionId: i.conditionId,
		question: i.question.trim().slice(0, QUESTION_MAX),
		addedAt: typeof i.addedAt === "number" && Number.isFinite(i.addedAt) && i.addedAt > 0 ? Math.min(i.addedAt, nowMs) : nowMs,
	};
	if (typeof i.addedPrice === "number" && i.addedPrice >= 0 && i.addedPrice <= 1) item.addedPrice = i.addedPrice;
	if (typeof i.outcome === "string" && i.outcome) item.outcome = i.outcome.slice(0, OUTCOME_MAX);
	return item;
}

/**
 * A file someone hands us is not trusted: every entry is rebuilt from checked fields and the
 * rest dropped. Null when it is not a watchlist at all (pure).
 */
export function parseImport(text: string, nowMs: number): { items: WatchItem[]; invalid: number } | null {
	let parsed: unknown;
	try {
		parsed = JSON.parse(text);
	} catch {
		return null;
	}
	const list = Array.isArray(parsed) ? parsed : (parsed as { items?: unknown } | null)?.items;
	if (!Array.isArray(list)) return null;
	const items = list.flatMap((raw) => {
		const item = cleanItem(raw, nowMs);
		return item ? [item] : [];
	});
	return { items, invalid: list.length - items.length };
}

/** Adds what is new and keeps what is pinned already, up to the limit (pure). */
export function mergeWatchlist(list: WatchItem[], incoming: WatchItem[]): { list: WatchItem[]; added: number; duplicate: number; overLimit: number } {
	const next = [...list];
	const seen = new Set(list.map((i) => i.conditionId));
	let added = 0;
	let duplicate = 0;
	let overLimit = 0;
	for (const item of incoming) {
		if (seen.has(item.conditionId)) duplicate += 1;
		else if (next.length >= WATCH_MAX) overLimit += 1;
		else {
			seen.add(item.conditionId);
			next.push(item);
			added += 1;
		}
	}
	return { list: next, added, duplicate, overLimit };
}

/** Price now minus price when pinned. Null when either is unknown (pure). */
export function moveSinceAdded(now: number | null | undefined, added: number | null | undefined): number | null {
	return typeof now === "number" && typeof added === "number" ? now - added : null;
}

function load(): WatchItem[] {
	try {
		return parseWatchlist(localStorage.getItem(KEY));
	} catch {
		return [];
	}
}

function save(list: WatchItem[]): void {
	try {
		localStorage.setItem(KEY, JSON.stringify(list));
	} catch {
		// Private windows can refuse storage. The list then lasts for this page view.
	}
	window.dispatchEvent(new CustomEvent(EVENT, { detail: list }));
}

export function useWatchlist() {
	const [items, setItems] = useState<WatchItem[]>(load);
	useEffect(() => {
		const onChange = (e: Event) => setItems((e as CustomEvent<WatchItem[]>).detail);
		const onStorage = (e: StorageEvent) => {
			if (e.key === KEY) setItems(parseWatchlist(e.newValue));
		};
		window.addEventListener(EVENT, onChange);
		window.addEventListener("storage", onStorage);
		return () => {
			window.removeEventListener(EVENT, onChange);
			window.removeEventListener("storage", onStorage);
		};
	}, []);
	return {
		items,
		has: (id: string) => items.some((i) => i.conditionId === id),
		toggle: (item: Omit<WatchItem, "addedAt">) => save(toggleItem(items, item, Date.now())),
		replace: save,
	};
}
